import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useDispatch } from 'react-redux';
import { unpinMessage } from 'src/redux/slices/whatsappV2';
import useScrollToMessage from 'src/hooks/useScrollToMessage';
import { COLORS } from './styles';

const wrapperSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.5,
    py: 0.75,
    backgroundColor: COLORS.warningBg,
    borderBottom: `1px solid ${COLORS.warningBorder}`,
    cursor: 'pointer',
    transition: 'background-color 120ms ease-out',
    '&:hover': { backgroundColor: 'rgba(255, 171, 0, 0.16)' },
};

const PinnedMessageBanner = ({ pinnedMessage, threadId }) => {
    const dispatch = useDispatch();
    const { scrollToMessage } = useScrollToMessage();

    if (!pinnedMessage || !pinnedMessage.id) {
        return null;
    }

    const handleJump = () => {
        scrollToMessage(pinnedMessage.id);
    };

    const handleUnpin = (event) => {
        // Prevent the wrapper click (jump) from also firing.
        event.stopPropagation();
        if (threadId != null) {
            dispatch(unpinMessage(threadId));
        }
    };

    // Track B fixup B-3 — backend serializer now returns `is_revoked`
    // (and no longer leaks `from_waid`). When the pinned message has
    // been revoked since pin time we surface a placeholder so operators
    // see something coherent instead of the original (now-deleted)
    // body. The pin row itself stays so an operator can dismiss it.
    const isRevoked = Boolean(pinnedMessage.is_revoked);
    const body = isRevoked
        ? '[Message deleted]'
        : pinnedMessage.body || '(no text)';

    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={handleJump}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleJump();
                }
            }}
            sx={wrapperSx}
            aria-label="Pinned message — tap to jump"
        >
            <Icon
                icon="mdi:pin"
                width={16}
                height={16}
                style={{ color: COLORS.warningDark, transform: 'rotate(45deg)', flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 11, color: COLORS.warningDark, lineHeight: 1, mb: 0.25 }}>
                    Pinned
                </Typography>
                <Typography
                    sx={{
                        fontSize: 12,
                        color: isRevoked ? COLORS.textDisabled : COLORS.textPrimary,
                        fontStyle: isRevoked ? 'italic' : 'normal',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {body}
                </Typography>
            </Box>
            <Tooltip title="Unpin">
                <IconButton
                    size="small"
                    onClick={handleUnpin}
                    aria-label="Unpin message"
                    sx={{ color: COLORS.warningDark, p: 0.25 }}
                >
                    <Icon icon="mdi:close" width={14} height={14} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

PinnedMessageBanner.propTypes = {
    // Track B fixup B-3 — `from_waid` removed from the backend
    // `WhatsappMessagePinnedSerializer` (PII tightening); FE shape
    // mirrors the new payload.
    pinnedMessage: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        body: PropTypes.string,
        created_time: PropTypes.string,
        is_revoked: PropTypes.bool,
    }),
    threadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PinnedMessageBanner.defaultProps = {
    pinnedMessage: null,
    threadId: null,
};

export default PinnedMessageBanner;
